import React, { Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { ToastContainer, Zoom } from 'react-toastify';
import { Router } from 'react-router-dom';

import './styles/index.scss';
import { GlobalLoader } from 'src/components/common/GlobalLoader';
import { CommonLogic } from 'src/containers/Common';
import { RoutesChecker } from 'src/routes';
import { store } from 'src/store/store';
import './utils/i18n';
import { ErrorBoundary } from 'src/components/common/ErrorBoundary';
import { customHistory } from 'src/utils/customHistory';
import { ChargebeeType } from 'src/models/chargebee';
import { SafeAnyType } from 'src/utils/safeAny';
import { TemplateGalleryModal } from 'src/components/Modals/TemplateGallery';
import { PersonalOrBusinessForBusinessModal } from 'src/components/Modals/PersonalOrBusinessForBusiness';
import { ExpiredCampaignsModal } from 'src/components/Modals/ExpiredCampaigns';

/** common variables */
declare global {
  interface Window {
    // inner props
    _useTestModals: true | undefined; // for setting modals in tests (with no use portals)
    // end inner props

    hideFooter: boolean;
    toolbarButton: {
      isToolbarButtonShown: boolean;
    };

    brandingModelInfo: {
      loginLogoUri: null | string;
      brandLogoUri: null | string;
      headerLogoLink: string;
      footerVisible: boolean;
      backColor?: string;
      UseWhiteIcons?: boolean;
      logoHeight?: number;
    };
    // eslint-disable-next-line camelcase
    beamer_config: {
      // eslint-disable-next-line camelcase
      product_id: string | null;
      selector: string | null;
      // eslint-disable-next-line camelcase
      user_firstname: string | null;
      // eslint-disable-next-line camelcase
      user_lastname: string | null;
      // eslint-disable-next-line camelcase
      user_email: string | null;
      // eslint-disable-next-line camelcase
      user_id: number | null;
      bounce: boolean | null;
    };

    Beamer?: {
      destroy: () => void;
    };

    /**
     * hack for opening a new window tab from any place later
     * @see prepareOpenNewWindow
     */
    openHackRef: { id: string | number; fn: Window | null }[];

    liveStreamCampaignInfo?: {
      campaignId: string; // is not used anymore
      permanentUrl: string;
      normalizeWidgets: boolean;
      pubnubChannel?: string; // todo get this via API
    };

    isInTestMode?: boolean;
    experiencePermanentUrl?: string;
    isDonationAllowed?: boolean;

    startSpa: (data?: { errorPageMessage: string | null }) => void;

    constants: {
      currencies: {
        padding: number;
        symbol: string;
        code: string;
      }[];
    };

    saveAs: (blob: Blob, name: string) => unknown; // FileSaver.js

    Chargebee: ChargebeeType;

    Stripe: (key: string, params: Record<string, SafeAnyType>) => SafeAnyType;

    Razorpay: SafeAnyType;

    managedCampaignSettings?: {
      organizationId: number | null;
      userId: number | null;
      isCreatedByAdmin: boolean;
    };
  }
}

/** start react manually */
window.startSpa = (startData) => {
  createRoot(document.getElementById('root') as HTMLElement).render(
    <React.StrictMode>
      <Provider store={store}>
        <ErrorBoundary>
          <Suspense fallback={<div />}>
            <ToastContainer
              position="bottom-left"
              autoClose={5000}
              transition={Zoom}
              draggable={false}
              hideProgressBar
              icon={false}
              theme="colored"
            />
            <GlobalLoader /> {/** needed? */}
            <Router history={customHistory}>
              <CommonLogic>
                <RoutesChecker errorPageMessage={startData?.errorPageMessage} />
                <TemplateGalleryModal />
                <PersonalOrBusinessForBusinessModal />
                <ExpiredCampaignsModal />
              </CommonLogic>
            </Router>
          </Suspense>
        </ErrorBoundary>
      </Provider>
    </React.StrictMode>
  );
};
window.openHackRef = [];

// run in dev at once
if (process.env.NODE_ENV === 'development') {
  import('src/utils/csrf')
    .then(async (module) => {
      await module.setVerificationToken();
      window.startSpa();
    })
    .catch(() => window.startSpa());
}
